import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AboutUsSectionResponseDto, HomePageChampionSectionResponseDto, HomePageFaqItemResponseDto, PageSettingsService } from './page-settings.service';

const HOME_SETTING_KEY = {
  HERO_IMAGE_UUID: 'home_page.hero.image_uuid',
  BUTTON_LINK: 'home_page.hero.button_link',
  HEADER_COLOR: 'home_page.hero.header_color',
  SUBHEADER_COLOR: 'home_page.hero.subheader_color',
  STAT_BANNER_BG_COLOR: 'home_page.hero.stat_header_bg_color',
  STAT_COLOR: 'home_page.hero.stat_header_color',
  STAT_BANNER_VALUE: 'home_page.hero.stat_banner_value',
  ABOUT_US_MEDIA_UUID_1: 'home_page.about_us.media_uuid_1',
  ABOUT_US_MEDIA_UUID_2: 'home_page.about_us.media_uuid_2',
  ABOUT_US_BUTTON_LINK: 'home_page.about_us.button_link',
  THREE_STEP_STEP1_IMAGE_UUID: 'home_page.three_step.step1.image_uuid',
  THREE_STEP_STEP2_IMAGE_UUID: 'home_page.three_step.step2.image_uuid',
  THREE_STEP_STEP3_IMAGE_UUID: 'home_page.three_step.step3.image_uuid',
  FAQ_BUTTON_LINK: 'home_page.faq.button_link',
  CTA_TOP_IMAGE_UUID: 'home_page.closing_cta.top_image_uuid',
  CTA_BOTTOM_IMAGE_UUID: 'home_page.closing_cta.bottom_image_uuid',
  CTA_BUTTON_LINK: 'home_page.closing_cta.button_link',
  CTA_HEADER_COLOR: 'home_page.closing_cta.header_color',
  CTA_SUBHEADER_COLOR: 'home_page.closing_cta.subheader_color',
} as const;

const SEARCH_RESULTS_SETTING_KEY = {
  HERO_HEADER_COLOR:     'search_results_page.hero.header_color',
  HERO_SUBHEADER_COLOR:  'search_results_page.hero.subheader_color',
  HERO_BG_COLOR:         'search_results_page.hero.background_color',
  CTA_TOP_IMAGE_UUID:    'search_results_page.closing_cta.top_image_uuid',
  CTA_BOTTOM_IMAGE_UUID: 'search_results_page.closing_cta.bottom_image_uuid',
  CTA_BUTTON_URL:        'search_results_page.closing_cta.button_url',
  CTA_HEADER_COLOR:      'search_results_page.closing_cta.header_color',
  CTA_SUBHEADER_COLOR:   'search_results_page.closing_cta.subheader_color',
} as const;

const PROJECT_DETAILS_SETTING_KEY = {
  HERO_TEXT_COLOR:      'project_details_page.hero.text_color',
  CTA_TOP_IMAGE_UUID:   'project_details_page.closing_cta.top_image_uuid',
  CTA_BOTTOM_IMAGE_UUID:'project_details_page.closing_cta.bottom_image_uuid',
  CTA_BUTTON_URL:       'project_details_page.closing_cta.button_url',
  CTA_HEADER_COLOR:     'project_details_page.closing_cta.header_color',
  CTA_SUBHEADER_COLOR:  'project_details_page.closing_cta.subheader_color',
} as const;

const CHARITY_DETAILS_SETTING_KEY = {
  HERO_TEXT_COLOR:      'charity_details_page.hero.text_color',
  CTA_TOP_IMAGE_UUID:   'charity_details_page.closing_cta.top_image_uuid',
  CTA_BOTTOM_IMAGE_UUID:'charity_details_page.closing_cta.bottom_image_uuid',
  CTA_BUTTON_URL:       'charity_details_page.closing_cta.button_url',
  CTA_HEADER_COLOR:     'charity_details_page.closing_cta.header_color',
  CTA_SUBHEADER_COLOR:  'charity_details_page.closing_cta.subheader_color',
} as const;

const PROJECT_CATALOG_SETTING_KEY = {
  HERO_HEADER_COLOR:     'project_catalog_page.hero.header_color',
  HERO_SUBHEADER_COLOR:  'project_catalog_page.hero.subheader_color',
  HERO_BG_COLOR:         'project_catalog_page.hero.background_color',
  CTA_TOP_IMAGE_UUID:    'project_catalog_page.closing_cta.top_image_uuid',
  CTA_BOTTOM_IMAGE_UUID: 'project_catalog_page.closing_cta.bottom_image_uuid',
  CTA_BUTTON_URL:        'project_catalog_page.closing_cta.button_url',
  CTA_HEADER_COLOR:      'project_catalog_page.closing_cta.header_color',
  CTA_SUBHEADER_COLOR:   'project_catalog_page.closing_cta.subheader_color',
} as const;

const CHARITY_CATALOG_SETTING_KEY = {
  HERO_HEADER_COLOR:     'charity_catalog_page.hero.header_color',
  HERO_SUBHEADER_COLOR:  'charity_catalog_page.hero.subheader_color',
  HERO_BG_COLOR:         'charity_catalog_page.hero.background_color',
  CTA_TOP_IMAGE_UUID:    'charity_catalog_page.closing_cta.top_image_uuid',
  CTA_BOTTOM_IMAGE_UUID: 'charity_catalog_page.closing_cta.bottom_image_uuid',
  CTA_BUTTON_URL:        'charity_catalog_page.closing_cta.button_url',
  CTA_HEADER_COLOR:      'charity_catalog_page.closing_cta.header_color',
  CTA_SUBHEADER_COLOR:   'charity_catalog_page.closing_cta.subheader_color',
} as const;

const PROMOTIONAL_TOOLS_SETTING_KEY = {
  MEDIA_KIT_GUIDELINES_URL:  'promotional_tools_page.media_kit.guidelines_url',
  MEDIA_KIT_GUIDELINES_UUID: 'promotional_tools.media_kit.guidelines_uuid',
  CANVA_TEMPLATE_URL:        'promotional_tools_page.canva_template_url',
} as const;

const ABOUT_SETTING_KEY = {
  HERO_IMAGE_UUID: 'about_page.hero.image_uuid',
  HERO_HEADER_COLOR: 'about_page.hero.header_color',
  HERO_SUBHEADER_COLOR: 'about_page.hero.subheader_color',
  CTA_TOP_IMAGE_UUID: 'about_page.closing_cta.top_image_uuid',
  CTA_BOTTOM_IMAGE_UUID: 'about_page.closing_cta.bottom_image_uuid',
  CTA_BUTTON_URL: 'about_page.closing_cta.button_url',
  CTA_HEADER_COLOR: 'about_page.closing_cta.header_color',
  CTA_SUBHEADER_COLOR: 'about_page.closing_cta.subheader_color',
} as const;

export interface HomeHeroStat {
  value: string;
  aspect: string;
}

@Injectable({ providedIn: 'root' })
export class PageSettingsStateService {
  // ---- Home page hero ----
  readonly homeHeroImageUrl = signal<string | null>(null);
  readonly homeHeroButtonLink = signal<string | null>(null);
  readonly homeHeroHeaderColor = signal<string>('#ffffff');
  readonly homeHeroSubheaderColor = signal<string>('#ffffff');
  readonly homeHeroStatBannerBgColor = signal<string>('#96080b');
  readonly homeHeroStatColor = signal<string>('#ffffff');
  readonly homeHeroStats = signal<HomeHeroStat[]>([]);

  // ---- Home page About Us section ----
  readonly homeAboutUsMediaUrl1 = signal<string | null>(null);
  readonly homeAboutUsMediaUrl2 = signal<string | null>(null);
  readonly homeAboutUsButtonLink = signal<string | null>(null);

  // ---- Home page 3-step giving ----
  readonly homeThreeStepStep1IconUrl = signal<string | null>(null);
  readonly homeThreeStepStep2IconUrl = signal<string | null>(null);
  readonly homeThreeStepStep3IconUrl = signal<string | null>(null);

  // ---- Home page champions ----
  readonly homePageChampions = signal<HomePageChampionSectionResponseDto[]>([]);

  // ---- Home page FAQ ----
  readonly homePageFaqItems = signal<HomePageFaqItemResponseDto[]>([]);
  readonly homeFaqButtonLink = signal<string | null>(null);

  // ---- Home page Closing CTA ----
  readonly homeCtaTopImageUrl = signal<string | null>(null);
  readonly homeCtaBottomImageUrl = signal<string | null>(null);
  readonly homeCtaButtonLink = signal<string | null>(null);
  readonly homeCtaHeaderColor = signal<string | null>(null);
  readonly homeCtaSubheaderColor = signal<string | null>(null);

  // ---- Search results page hero ----
  readonly searchResultsHeroHeaderColor    = signal<string | null>(null);
  readonly searchResultsHeroSubheaderColor = signal<string | null>(null);
  readonly searchResultsHeroBgColor        = signal<string | null>(null);

  // ---- Search results page Closing CTA ----
  readonly searchResultsCtaTopImageUrl    = signal<string | null>(null);
  readonly searchResultsCtaBottomImageUrl = signal<string | null>(null);
  readonly searchResultsCtaButtonUrl      = signal<string | null>(null);
  readonly searchResultsCtaHeaderColor    = signal<string | null>(null);
  readonly searchResultsCtaSubheaderColor = signal<string | null>(null);

  // ---- Project details page hero ----
  readonly projectDetailsHeroTextColor    = signal<string | null>(null);

  // ---- Project details page Closing CTA ----
  readonly projectDetailsCtaTopImageUrl    = signal<string | null>(null);
  readonly projectDetailsCtaBottomImageUrl = signal<string | null>(null);
  readonly projectDetailsCtaButtonUrl      = signal<string | null>(null);
  readonly projectDetailsCtaHeaderColor    = signal<string | null>(null);
  readonly projectDetailsCtaSubheaderColor = signal<string | null>(null);

  // ---- Charity details page hero ----
  readonly charityDetailsHeroTextColor    = signal<string | null>(null);

  // ---- Charity details page Closing CTA ----
  readonly charityDetailsCtaTopImageUrl    = signal<string | null>(null);
  readonly charityDetailsCtaBottomImageUrl = signal<string | null>(null);
  readonly charityDetailsCtaButtonUrl      = signal<string | null>(null);
  readonly charityDetailsCtaHeaderColor    = signal<string | null>(null);
  readonly charityDetailsCtaSubheaderColor = signal<string | null>(null);

  // ---- Project catalog page hero ----
  readonly projectCatalogHeroHeaderColor    = signal<string | null>(null);
  readonly projectCatalogHeroSubheaderColor = signal<string | null>(null);
  readonly projectCatalogHeroBgColor        = signal<string | null>(null);

  // ---- Project catalog page Closing CTA ----
  readonly projectCatalogCtaTopImageUrl    = signal<string | null>(null);
  readonly projectCatalogCtaBottomImageUrl = signal<string | null>(null);
  readonly projectCatalogCtaButtonUrl      = signal<string | null>(null);
  readonly projectCatalogCtaHeaderColor    = signal<string | null>(null);
  readonly projectCatalogCtaSubheaderColor = signal<string | null>(null);

  // ---- Charity catalog page hero ----
  readonly charityCatalogHeroHeaderColor    = signal<string | null>(null);
  readonly charityCatalogHeroSubheaderColor = signal<string | null>(null);
  readonly charityCatalogHeroBgColor        = signal<string | null>(null);

  // ---- Charity catalog page Closing CTA ----
  readonly charityCatalogCtaTopImageUrl    = signal<string | null>(null);
  readonly charityCatalogCtaBottomImageUrl = signal<string | null>(null);
  readonly charityCatalogCtaButtonUrl      = signal<string | null>(null);
  readonly charityCatalogCtaHeaderColor    = signal<string | null>(null);
  readonly charityCatalogCtaSubheaderColor = signal<string | null>(null);

  // ---- Promotional tools page ----
  readonly promotionalToolsMediaKitGuidelinesUrl = signal<string | null>(null);
  readonly promotionalToolsCanvaTemplateUrl      = signal<string | null>(null);

  // ---- About page hero ----
  readonly aboutHeroImageUrl = signal<string | null>(null);
  readonly aboutHeroHeaderColor = signal<string>('#a78f65');
  readonly aboutHeroSubheaderColor = signal<string>('#ffffff');
  readonly aboutSections = signal<AboutUsSectionResponseDto[]>([]);
  readonly ctaTopImageUrl = signal<string | null>(null);
  readonly ctaBottomImageUrl = signal<string | null>(null);
  readonly ctaButtonUrl = signal<string | null>(null);
  readonly ctaHeaderColor = signal<string | null>(null);
  readonly ctaSubheaderColor = signal<string | null>(null);

  constructor(private pageSettingsService: PageSettingsService) {}

  private parseHomeHeroStats(raw: string): HomeHeroStat[] {
    if (!raw) return [];
    const inner = raw.replace(/^\{/, '').replace(/\}$/, '').trim();
    if (!inner) return [];
    return inner.split(',').map((entry) => {
      const eqIdx = entry.indexOf('=');
      if (eqIdx === -1) return { value: entry.trim(), aspect: '' };
      return { value: entry.slice(0, eqIdx).trim(), aspect: entry.slice(eqIdx + 1).trim() };
    });
  }

  initialize(): Promise<void> {
    return firstValueFrom(this.pageSettingsService.getAll())
      .then(({ response }) => {
        const settingsMap = new Map(response.settings.map((s) => [s.key, s]));

        // ---- Home page hero ----
        const homeHeroImage = settingsMap.get(HOME_SETTING_KEY.HERO_IMAGE_UUID);
        if (homeHeroImage?.resolvedUrl) this.homeHeroImageUrl.set(homeHeroImage.resolvedUrl);

        const homeButtonLink = settingsMap.get(HOME_SETTING_KEY.BUTTON_LINK);
        if (homeButtonLink?.value) this.homeHeroButtonLink.set(homeButtonLink.value);

        const homeHeaderColor = settingsMap.get(HOME_SETTING_KEY.HEADER_COLOR);
        if (homeHeaderColor?.value) this.homeHeroHeaderColor.set(homeHeaderColor.value);

        const homeSubheaderColor = settingsMap.get(HOME_SETTING_KEY.SUBHEADER_COLOR);
        if (homeSubheaderColor?.value) this.homeHeroSubheaderColor.set(homeSubheaderColor.value);

        const homeStatBannerBgColor = settingsMap.get(HOME_SETTING_KEY.STAT_BANNER_BG_COLOR);
        if (homeStatBannerBgColor?.value) this.homeHeroStatBannerBgColor.set(homeStatBannerBgColor.value);

        const homeStatColor = settingsMap.get(HOME_SETTING_KEY.STAT_COLOR);
        if (homeStatColor?.value) this.homeHeroStatColor.set(homeStatColor.value);

        const homeStatBannerValue = settingsMap.get(HOME_SETTING_KEY.STAT_BANNER_VALUE);
        if (homeStatBannerValue?.value) {
          this.homeHeroStats.set(this.parseHomeHeroStats(homeStatBannerValue.value));
        }

        // ---- Home page About Us section ----
        const homeAboutUsMedia1 = settingsMap.get(HOME_SETTING_KEY.ABOUT_US_MEDIA_UUID_1);
        if (homeAboutUsMedia1?.resolvedUrl) this.homeAboutUsMediaUrl1.set(homeAboutUsMedia1.resolvedUrl);

        const homeAboutUsMedia2 = settingsMap.get(HOME_SETTING_KEY.ABOUT_US_MEDIA_UUID_2);
        if (homeAboutUsMedia2?.resolvedUrl) this.homeAboutUsMediaUrl2.set(homeAboutUsMedia2.resolvedUrl);

        const homeAboutUsButtonLink = settingsMap.get(HOME_SETTING_KEY.ABOUT_US_BUTTON_LINK);
        if (homeAboutUsButtonLink?.value) this.homeAboutUsButtonLink.set(homeAboutUsButtonLink.value);

        // ---- Home page 3-step giving ----
        const step1Icon = settingsMap.get(HOME_SETTING_KEY.THREE_STEP_STEP1_IMAGE_UUID);
        if (step1Icon?.resolvedUrl) this.homeThreeStepStep1IconUrl.set(step1Icon.resolvedUrl);

        const step2Icon = settingsMap.get(HOME_SETTING_KEY.THREE_STEP_STEP2_IMAGE_UUID);
        if (step2Icon?.resolvedUrl) this.homeThreeStepStep2IconUrl.set(step2Icon.resolvedUrl);

        const step3Icon = settingsMap.get(HOME_SETTING_KEY.THREE_STEP_STEP3_IMAGE_UUID);
        if (step3Icon?.resolvedUrl) this.homeThreeStepStep3IconUrl.set(step3Icon.resolvedUrl);

        // ---- Home page champions ----
        this.homePageChampions.set(response.homePageChampions ?? []);

        // ---- Home page FAQ ----
        this.homePageFaqItems.set(response.homePageFaqItems ?? []);
        const faqButtonLink = settingsMap.get(HOME_SETTING_KEY.FAQ_BUTTON_LINK);
        if (faqButtonLink?.value) this.homeFaqButtonLink.set(faqButtonLink.value);

        // ---- Home page Closing CTA ----
        const homeCtaTopImage = settingsMap.get(HOME_SETTING_KEY.CTA_TOP_IMAGE_UUID);
        if (homeCtaTopImage?.resolvedUrl) this.homeCtaTopImageUrl.set(homeCtaTopImage.resolvedUrl);

        const homeCtaBottomImage = settingsMap.get(HOME_SETTING_KEY.CTA_BOTTOM_IMAGE_UUID);
        if (homeCtaBottomImage?.resolvedUrl) this.homeCtaBottomImageUrl.set(homeCtaBottomImage.resolvedUrl);

        const homeCtaButtonLink = settingsMap.get(HOME_SETTING_KEY.CTA_BUTTON_LINK);
        if (homeCtaButtonLink?.value) this.homeCtaButtonLink.set(homeCtaButtonLink.value);

        const homeCtaHeaderColor = settingsMap.get(HOME_SETTING_KEY.CTA_HEADER_COLOR);
        if (homeCtaHeaderColor?.value) this.homeCtaHeaderColor.set(homeCtaHeaderColor.value);

        const homeCtaSubheaderColor = settingsMap.get(HOME_SETTING_KEY.CTA_SUBHEADER_COLOR);
        if (homeCtaSubheaderColor?.value) this.homeCtaSubheaderColor.set(homeCtaSubheaderColor.value);

        // ---- Search results page hero ----
        const srHeroHeaderColor = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.HERO_HEADER_COLOR);
        if (srHeroHeaderColor?.value) this.searchResultsHeroHeaderColor.set(srHeroHeaderColor.value);

        const srHeroSubheaderColor = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.HERO_SUBHEADER_COLOR);
        if (srHeroSubheaderColor?.value) this.searchResultsHeroSubheaderColor.set(srHeroSubheaderColor.value);

        const srHeroBgColor = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.HERO_BG_COLOR);
        if (srHeroBgColor?.value) this.searchResultsHeroBgColor.set(srHeroBgColor.value);

        // ---- Search results page Closing CTA ----
        const srCtaTopImage = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.CTA_TOP_IMAGE_UUID);
        if (srCtaTopImage?.resolvedUrl) this.searchResultsCtaTopImageUrl.set(srCtaTopImage.resolvedUrl);

        const srCtaBottomImage = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.CTA_BOTTOM_IMAGE_UUID);
        if (srCtaBottomImage?.resolvedUrl) this.searchResultsCtaBottomImageUrl.set(srCtaBottomImage.resolvedUrl);

        const srCtaButtonUrl = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.CTA_BUTTON_URL);
        if (srCtaButtonUrl?.value) this.searchResultsCtaButtonUrl.set(srCtaButtonUrl.value);

        const srCtaHeaderColor = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.CTA_HEADER_COLOR);
        if (srCtaHeaderColor?.value) this.searchResultsCtaHeaderColor.set(srCtaHeaderColor.value);

        const srCtaSubheaderColor = settingsMap.get(SEARCH_RESULTS_SETTING_KEY.CTA_SUBHEADER_COLOR);
        if (srCtaSubheaderColor?.value) this.searchResultsCtaSubheaderColor.set(srCtaSubheaderColor.value);

        // ---- Project details page hero ----
        const projectHeroTextColor = settingsMap.get(PROJECT_DETAILS_SETTING_KEY.HERO_TEXT_COLOR);
        if (projectHeroTextColor?.value) this.projectDetailsHeroTextColor.set(projectHeroTextColor.value);

        // ---- Project details page Closing CTA ----
        const projectCtaTopImage = settingsMap.get(PROJECT_DETAILS_SETTING_KEY.CTA_TOP_IMAGE_UUID);
        if (projectCtaTopImage?.resolvedUrl) this.projectDetailsCtaTopImageUrl.set(projectCtaTopImage.resolvedUrl);

        const projectCtaBottomImage = settingsMap.get(PROJECT_DETAILS_SETTING_KEY.CTA_BOTTOM_IMAGE_UUID);
        if (projectCtaBottomImage?.resolvedUrl) this.projectDetailsCtaBottomImageUrl.set(projectCtaBottomImage.resolvedUrl);

        const projectCtaButtonUrl = settingsMap.get(PROJECT_DETAILS_SETTING_KEY.CTA_BUTTON_URL);
        if (projectCtaButtonUrl?.value) this.projectDetailsCtaButtonUrl.set(projectCtaButtonUrl.value);

        const projectCtaHeaderColor = settingsMap.get(PROJECT_DETAILS_SETTING_KEY.CTA_HEADER_COLOR);
        if (projectCtaHeaderColor?.value) this.projectDetailsCtaHeaderColor.set(projectCtaHeaderColor.value);

        const projectCtaSubheaderColor = settingsMap.get(PROJECT_DETAILS_SETTING_KEY.CTA_SUBHEADER_COLOR);
        if (projectCtaSubheaderColor?.value) this.projectDetailsCtaSubheaderColor.set(projectCtaSubheaderColor.value);

        // ---- Charity details page hero ----
        const charityHeroTextColor = settingsMap.get(CHARITY_DETAILS_SETTING_KEY.HERO_TEXT_COLOR);
        if (charityHeroTextColor?.value) this.charityDetailsHeroTextColor.set(charityHeroTextColor.value);

        // ---- Charity details page Closing CTA ----
        const charityCtaTopImage = settingsMap.get(CHARITY_DETAILS_SETTING_KEY.CTA_TOP_IMAGE_UUID);
        if (charityCtaTopImage?.resolvedUrl) this.charityDetailsCtaTopImageUrl.set(charityCtaTopImage.resolvedUrl);

        const charityCtaBottomImage = settingsMap.get(CHARITY_DETAILS_SETTING_KEY.CTA_BOTTOM_IMAGE_UUID);
        if (charityCtaBottomImage?.resolvedUrl) this.charityDetailsCtaBottomImageUrl.set(charityCtaBottomImage.resolvedUrl);

        const charityCtaButtonUrl = settingsMap.get(CHARITY_DETAILS_SETTING_KEY.CTA_BUTTON_URL);
        if (charityCtaButtonUrl?.value) this.charityDetailsCtaButtonUrl.set(charityCtaButtonUrl.value);

        const charityCtaHeaderColor = settingsMap.get(CHARITY_DETAILS_SETTING_KEY.CTA_HEADER_COLOR);
        if (charityCtaHeaderColor?.value) this.charityDetailsCtaHeaderColor.set(charityCtaHeaderColor.value);

        const charityCtaSubheaderColor = settingsMap.get(CHARITY_DETAILS_SETTING_KEY.CTA_SUBHEADER_COLOR);
        if (charityCtaSubheaderColor?.value) this.charityDetailsCtaSubheaderColor.set(charityCtaSubheaderColor.value);

        // ---- Project catalog page hero ----
        const pcHeroHeaderColor = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.HERO_HEADER_COLOR);
        if (pcHeroHeaderColor?.value) this.projectCatalogHeroHeaderColor.set(pcHeroHeaderColor.value);

        const pcHeroSubheaderColor = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.HERO_SUBHEADER_COLOR);
        if (pcHeroSubheaderColor?.value) this.projectCatalogHeroSubheaderColor.set(pcHeroSubheaderColor.value);

        const pcHeroBgColor = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.HERO_BG_COLOR);
        if (pcHeroBgColor?.value) this.projectCatalogHeroBgColor.set(pcHeroBgColor.value);

        // ---- Project catalog page Closing CTA ----
        const pcCtaTopImage = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.CTA_TOP_IMAGE_UUID);
        if (pcCtaTopImage?.resolvedUrl) this.projectCatalogCtaTopImageUrl.set(pcCtaTopImage.resolvedUrl);

        const pcCtaBottomImage = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.CTA_BOTTOM_IMAGE_UUID);
        if (pcCtaBottomImage?.resolvedUrl) this.projectCatalogCtaBottomImageUrl.set(pcCtaBottomImage.resolvedUrl);

        const pcCtaButtonUrl = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.CTA_BUTTON_URL);
        if (pcCtaButtonUrl?.value) this.projectCatalogCtaButtonUrl.set(pcCtaButtonUrl.value);

        const pcCtaHeaderColor = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.CTA_HEADER_COLOR);
        if (pcCtaHeaderColor?.value) this.projectCatalogCtaHeaderColor.set(pcCtaHeaderColor.value);

        const pcCtaSubheaderColor = settingsMap.get(PROJECT_CATALOG_SETTING_KEY.CTA_SUBHEADER_COLOR);
        if (pcCtaSubheaderColor?.value) this.projectCatalogCtaSubheaderColor.set(pcCtaSubheaderColor.value);

        // ---- Charity catalog page hero ----
        const ccHeroHeaderColor = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.HERO_HEADER_COLOR);
        if (ccHeroHeaderColor?.value) this.charityCatalogHeroHeaderColor.set(ccHeroHeaderColor.value);

        const ccHeroSubheaderColor = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.HERO_SUBHEADER_COLOR);
        if (ccHeroSubheaderColor?.value) this.charityCatalogHeroSubheaderColor.set(ccHeroSubheaderColor.value);

        const ccHeroBgColor = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.HERO_BG_COLOR);
        if (ccHeroBgColor?.value) this.charityCatalogHeroBgColor.set(ccHeroBgColor.value);

        // ---- Charity catalog page Closing CTA ----
        const ccCtaTopImage = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.CTA_TOP_IMAGE_UUID);
        if (ccCtaTopImage?.resolvedUrl) this.charityCatalogCtaTopImageUrl.set(ccCtaTopImage.resolvedUrl);

        const ccCtaBottomImage = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.CTA_BOTTOM_IMAGE_UUID);
        if (ccCtaBottomImage?.resolvedUrl) this.charityCatalogCtaBottomImageUrl.set(ccCtaBottomImage.resolvedUrl);

        const ccCtaButtonUrl = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.CTA_BUTTON_URL);
        if (ccCtaButtonUrl?.value) this.charityCatalogCtaButtonUrl.set(ccCtaButtonUrl.value);

        const ccCtaHeaderColor = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.CTA_HEADER_COLOR);
        if (ccCtaHeaderColor?.value) this.charityCatalogCtaHeaderColor.set(ccCtaHeaderColor.value);

        const ccCtaSubheaderColor = settingsMap.get(CHARITY_CATALOG_SETTING_KEY.CTA_SUBHEADER_COLOR);
        if (ccCtaSubheaderColor?.value) this.charityCatalogCtaSubheaderColor.set(ccCtaSubheaderColor.value);

        // ---- Promotional tools page ----
        // Prefer the uploaded document (guidelines_uuid resolvedUrl); fall back to
        // the manually entered link URL when no document is uploaded.
        const ptMediaKitUuid = settingsMap.get(PROMOTIONAL_TOOLS_SETTING_KEY.MEDIA_KIT_GUIDELINES_UUID);
        const ptMediaKitUrl = settingsMap.get(PROMOTIONAL_TOOLS_SETTING_KEY.MEDIA_KIT_GUIDELINES_URL);
        if (ptMediaKitUuid?.resolvedUrl) {
          this.promotionalToolsMediaKitGuidelinesUrl.set(ptMediaKitUuid.resolvedUrl);
        } else if (ptMediaKitUrl?.value) {
          this.promotionalToolsMediaKitGuidelinesUrl.set(ptMediaKitUrl.value);
        }

        const ptCanvaUrl = settingsMap.get(PROMOTIONAL_TOOLS_SETTING_KEY.CANVA_TEMPLATE_URL);
        if (ptCanvaUrl?.value) this.promotionalToolsCanvaTemplateUrl.set(ptCanvaUrl.value);

        // ---- About page hero ----
        const heroImage = settingsMap.get(ABOUT_SETTING_KEY.HERO_IMAGE_UUID);
        if (heroImage?.resolvedUrl) this.aboutHeroImageUrl.set(heroImage.resolvedUrl);

        const headerColor = settingsMap.get(ABOUT_SETTING_KEY.HERO_HEADER_COLOR);
        if (headerColor?.value) this.aboutHeroHeaderColor.set(headerColor.value);

        const subheaderColor = settingsMap.get(ABOUT_SETTING_KEY.HERO_SUBHEADER_COLOR);
        if (subheaderColor?.value) this.aboutHeroSubheaderColor.set(subheaderColor.value);

        this.aboutSections.set(response.aboutPageSections ?? []);

        const ctaTopImage = settingsMap.get(ABOUT_SETTING_KEY.CTA_TOP_IMAGE_UUID);
        if (ctaTopImage?.resolvedUrl) this.ctaTopImageUrl.set(ctaTopImage.resolvedUrl);

        const ctaBottomImage = settingsMap.get(ABOUT_SETTING_KEY.CTA_BOTTOM_IMAGE_UUID);
        if (ctaBottomImage?.resolvedUrl) this.ctaBottomImageUrl.set(ctaBottomImage.resolvedUrl);

        const ctaButtonUrl = settingsMap.get(ABOUT_SETTING_KEY.CTA_BUTTON_URL);
        if (ctaButtonUrl?.value) this.ctaButtonUrl.set(ctaButtonUrl.value);

        const ctaHeaderColor = settingsMap.get(ABOUT_SETTING_KEY.CTA_HEADER_COLOR);
        if (ctaHeaderColor?.value) this.ctaHeaderColor.set(ctaHeaderColor.value);

        const ctaSubheaderColor = settingsMap.get(ABOUT_SETTING_KEY.CTA_SUBHEADER_COLOR);
        if (ctaSubheaderColor?.value) this.ctaSubheaderColor.set(ctaSubheaderColor.value);
      })
      .catch(() => {
        // Non-critical — keep defaults on error
      });
  }

}
